/**
 * Frozen Type Contracts for Sonar Quiz System
 * Version: 1.1.0 (Fixed PageCache with answers field)
 *
 * These types are FROZEN and must not be modified without version bump.
 * Any changes require migration strategy and backwards compatibility.
 *
 * Changelog:
 * - 1.1.0: Added missing `answers` field to PageCache (fixes 78 eslint-disable comments)
 * - 1.0.0: Initial contracts
 */

// ============================================================================
// CORE IDENTIFIERS
// ============================================================================

/** Release identifier format: "MM-YYYY" */
export type ReleaseId = string;

/** Service ID for student identification */
export type ServiceId = string;

/** Page identifier from DITA document */
export type PageId = string;

/** Table identifier (16-char hash based on table structure: rows x cols + class name) */
export type TableId = string;

/** Cell key format: "R{row}C{col}#f:{hash}" where hash is 8-char from normalized content */
export type CellKey = string;

// ============================================================================
// ENUMERATIONS
// ============================================================================

/** Page completion state */
export type CompletionState = 'unstarted' | 'incomplete' | 'complete';

/** Question type in quiz */
export type QuestionKind = 'mcq' | 'numeric';

// ============================================================================
// QUIZ ENTITIES
// ============================================================================

/** Individual quiz answer with correctness */
export interface AnswerRecord {
  /** User's answer value */
  answer: string;
  /** Whether the answer is correct */
  success: boolean;
  /** Timestamp when answer was submitted (ISO 8601) */
  timestamp: string;
  /**
   * Answers the student committed for this question: an MCQ choice, or a
   * numeric value when the field lost focus (v3; SRD 013 SCO-01). 0 while a
   * first value is still being typed. Absent on records written before v3.
   */
  attempts?: number;
  /** Whether the first committed answer was correct (v3; absent before v3) */
  firstSuccess?: boolean;
}

/** Quiz question definition */
export interface QuizQuestion {
  /** Question text */
  text: string;
  /** Question type */
  kind: QuestionKind;
  /** Correct answer */
  correctAnswer: string;
  /** MCQ options (for mcq type) */
  options?: string[];
  /** Numeric tolerance (for numeric type) */
  tolerance?: number;
}

// ============================================================================
// ANALYSIS ENTITIES
// ============================================================================

/** Analysis table data */
export interface AnalysisData {
  /** Unique table identifier */
  tableId: TableId;
  /** Cell key to content mapping */
  cells: Record<CellKey, string>;
  /** First edit timestamp (ISO 8601) */
  firstEdited?: string;
  /** Last edit timestamp (ISO 8601) */
  lastEdited?: string;
  /** Number of editable cells in the table, so completeness can be judged (v3) */
  totalCells?: number;
  /** Row label of each edited cell, e.g. "Threat Assessment" (v3; SRD 013 ANA-03) */
  labels?: Record<CellKey, string>;
}

// ============================================================================
// PAGE DATA
// ============================================================================

/** Student's data for a specific page */
export interface PageData {
  /** Array of quiz answers */
  answers: AnswerRecord[];
  /** Calculated completion state */
  state: CompletionState;
  /** First attempt timestamp (ISO 8601) */
  firstAttempted?: string;
  /** Last attempt timestamp (ISO 8601) */
  lastAttempted?: string;
  /** Analysis table data if present */
  analysis?: AnalysisData;
  /** Number of quiz questions on the page when last answered (v3) */
  totalQuestions?: number;
}

// ============================================================================
// STUDENT RECORD
// ============================================================================

/** Complete student progress record */
export interface StudentRecord {
  /** Schema version for migrations */
  schema: number;
  /** Document identifier */
  docId: string;
  /** Release version */
  release: ReleaseId;
  /** Student service ID */
  serviceId: ServiceId;
  /** Student name */
  name: string;
  /** Total questions attempted */
  attempted: number;
  /** Total correct answers */
  correct: number;
  /** Last update timestamp (ISO 8601) */
  updated: string;
  /** Page data by page ID */
  pages: Record<PageId, PageData>;

  // PIN Authentication (v2, salted KDF in v3)
  /**
   * PIN verifier: hex PBKDF2-SHA-256 output when `pinKdf` is set (v3), or a
   * legacy unsalted SHA-256 hex digest (v2) until the next successful login
   * upgrades it
   */
  pinHash?: string;
  /** Hex-encoded random salt for the PIN KDF (v3) */
  pinSalt?: string;
  /** PBKDF2 iteration count used for this record's PIN (v3) */
  pinIterations?: number;
  /** PIN key-derivation function; absent means legacy SHA-256 (v3) */
  pinKdf?: 'pbkdf2-sha256';
  /** ISO 8601 timestamp when PIN was created */
  pinCreatedAt?: string;
  /** ISO 8601 timestamp when PIN was last reset */
  pinResetAt?: string;
}

// ============================================================================
// PIN AUTHENTICATION (v2)
// ============================================================================

/** Rate limiting state for PIN attempts (stored in sessionStorage) */
export interface PinAttemptState {
  /** Student identifier */
  serviceId: ServiceId;
  /** Failed attempt count (0-3) */
  attempts: number;
  /** ISO 8601 timestamp when lockout expires, or null */
  lockoutUntil: string | null;
  /** ISO 8601 timestamp of last attempt */
  lastAttempt: string;
}

/** Audit trail for instructor PIN resets (stored in IndexedDB) */
export interface PinResetEvent {
  /** UUID v4 */
  eventId: string;
  /** Student affected */
  serviceId: ServiceId;
  /** Actor type */
  resetBy: 'instructor';
  /** ISO 8601 timestamp */
  resetAt: string;
  /** Context */
  release: ReleaseId;
}

// ============================================================================
// SESSION MANAGEMENT
// ============================================================================

/**
 * Active session data
 *
 * Note: serviceId and release are duplicated from the storage key
 * for convenient access without requiring a storage lookup
 */
export interface SessionData {
  /** Student service ID (duplicated from storage key) */
  serviceId: ServiceId;
  /** Student name */
  name: string;
  /** Current release (duplicated from storage key) */
  release: ReleaseId;
  /** Login timestamp (ISO 8601) */
  loginTime: string;
  /** Last activity timestamp (ISO 8601) */
  lastActivity: string;
  /** Session expiry timestamp (ISO 8601) */
  expiresAt: string;
  /** Whether instructor mode is unlocked */
  instructorUnlocked: boolean;
  /** Instructor unlock timestamp (ISO 8601) */
  unlockTime?: string;
}

/**
 * Cached page state for performance
 *
 * CRITICAL FIX: Added `answers` field to fix type safety issues
 * This was missing in v1.0.0, causing 78 eslint-disable comments
 */
export interface PageCache {
  /** Page completion state */
  state: CompletionState;
  /** Total number of questions registered on this page */
  total: number;
  /** Number of questions answered */
  answered: number;
  /** Number of correct answers */
  correct: number;
  /** Last update timestamp (ISO 8601) */
  last?: string;
  /** Answer records (ADDED in v1.1.0) */
  answers?: AnswerRecord[];
  /** Analysis table data if present (ADDED in v1.2.0) */
  analysis?: AnalysisData;
}

/** Session cache for quick access */
export interface SessionCache {
  /** Aggregated totals */
  totals: {
    total: number;
    answered: number;
    correct: number;
  };
  /** Per-page cache */
  pages: Record<PageId, PageCache>;
}

// ============================================================================
// INSTRUCTOR FEATURES
// ============================================================================

/** Student summary for instructor view */
export interface StudentSummary {
  /** Student service ID */
  serviceId: ServiceId;
  /** Student name */
  name: string;
  /** Questions attempted */
  attempted: number;
  /** Correct answers */
  correct: number;
  /** Success percentage */
  percentage: number;
  /** Last activity timestamp */
  lastActive: string;
}

/** Quiz results export format */
export interface QuizExport {
  /** Export timestamp */
  timestamp: string;
  /** Release version */
  release: ReleaseId;
  /** Document ID */
  docId: string;
  /** Student results */
  students: StudentSummary[];
  /** Detailed answers by page */
  details?: {
    pageId: PageId;
    studentId: ServiceId;
    answers: AnswerRecord[];
  }[];
}

// ============================================================================
// DOM ENHANCEMENT
// ============================================================================

/** Quiz table parsing result */
export interface ParsedQuizTable {
  /** Table element reference */
  element: HTMLTableElement;
  /** Extracted questions */
  questions: QuizQuestion[];
  /** Validation errors if any */
  errors?: string[];
}

/** Analysis table parsing result */
export interface ParsedAnalysisTable {
  /** Table element reference */
  element: HTMLTableElement;
  /** Table identifier */
  tableId: TableId;
  /** Editable cell positions */
  editableCells: Array<{
    row: number;
    col: number;
    key: CellKey;
  }>;
  /** Validation errors if any */
  errors?: string[];
}

// ============================================================================
// STORAGE ADAPTER
// ============================================================================

/** Storage adapter interface for data persistence */
export interface StorageAdapter {
  /** Initialize storage */
  init(): Promise<void>;

  /** Get student record */
  getStudent(release: ReleaseId, serviceId: ServiceId): Promise<StudentRecord | null>;

  /** Save student record */
  saveStudent(record: StudentRecord): Promise<void>;

  /** Get all students for a release */
  getStudentsByRelease(release: ReleaseId): Promise<StudentRecord[]>;

  /** Delete all data */
  clearAll(): Promise<void>;

  /** Create backup */
  backup(record: StudentRecord): Promise<void>;
}

// ============================================================================
// EVENTS
// ============================================================================

/** Custom event namespace */
export const EVENT_NAMESPACE = 'qd';

/** Event type definitions */
export interface QuizEvents {
  'qd:login': { detail: SessionData };
  'qd:logout': { detail: { serviceId: ServiceId } };
  'qd:answer-saved': { detail: { pageId: PageId; answer: AnswerRecord } };
  'qd:state-changed': { detail: { pageId: PageId; state: CompletionState } };
  'qd:analysis-saved': {
    detail: { pageId: PageId; tableId: TableId; cellKey: CellKey; content: string };
  };
  'qd:instructor-unlock': { detail: { timestamp: string } };
  'qd:instructor-lock': { detail: { timestamp: string } };
  'qd:data-cleared': { detail: { timestamp: string } };
  'qd:session-expired': { detail: { timestamp: string } };
  'qd:storage-error': { detail: { error: Error; operation: string } };
  // PIN Authentication events (v2)
  'qd:pin-created': { detail: { serviceId: ServiceId; timestamp: string } };
  'qd:pin-verified': { detail: { serviceId: ServiceId; timestamp: string } };
  'qd:pin-reset': { detail: { serviceId: ServiceId; resetBy: 'instructor'; timestamp: string } };
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Current schema version.
 *
 * v3 (SRD 013) adds only optional fields (salted PIN KDF, answer attempts,
 * analysis labels), so v2 records stay valid and are stamped v3 lazily.
 */
export const SCHEMA_VERSION = 3;

/**
 * First schema version that carries a student PIN.
 *
 * Records below this need the PIN-setup flow. Kept separate from
 * {@link SCHEMA_VERSION} so later bumps never send PIN-bearing records
 * back through PIN setup.
 */
export const PIN_SCHEMA_VERSION = 2;

/**
 * Session timeout in milliseconds (10 minutes; SRD 013 SEC-09).
 *
 * Students share one machine, so an abandoned session must expire before
 * the next student is likely to sit down.
 */
export const SESSION_TIMEOUT_MS = 10 * 60 * 1000;

/** Storage keys */
export const STORAGE_KEYS = {
  SESSION: 'qd/session',
  CACHE: 'qd/state',
  INSTRUCTOR: 'qd/instructor',
  PIN_ATTEMPTS: 'qd:pin-attempts',
  /** Set when a session ends by idle timeout, so the login form can say so (SRD 013 SEC-10) */
  SESSION_EXPIRED: 'qd:session-expired',
} as const;

/** PIN authentication constants */
export const PIN_CONSTANTS = {
  /** Maximum failed attempts before lockout */
  MAX_ATTEMPTS: 3,
  /** Lockout duration in milliseconds (30 seconds) */
  LOCKOUT_MS: 30 * 1000,
  /** PIN length (must be exactly 4 digits) */
  PIN_LENGTH: 4,
  /** PBKDF2-SHA-256 iterations for new PIN verifiers (SRD 013 SEC-06) */
  KDF_ITERATIONS: 600_000,
  /** Random salt length in bytes for the PIN KDF */
  SALT_BYTES: 16,
} as const;

/** CSS classes for DOM selection */
export const CSS_CLASSES = {
  QUIZ_TABLE: 'qd-quiz',
  ANALYSIS_TABLE: 'qd-analysis',
  TEST_LINK: 'quizPageBtn',
} as const;

/** Element IDs */
export const ELEMENT_IDS = {
  STATUS_PANEL: 'qd-status',
} as const;

/**
 * CSS selectors for DOM injection points
 *
 * These are default/reference values. Actual selectors are configurable
 * via SonarQuizConfig.statusPanelContainer option.
 *
 * @see SonarQuizConfig in src/index.ts
 */
export const INJECTION_SELECTORS = {
  /** Default navbar container for Oxygen WebHelp templates */
  NAVBAR_CONTAINER: '.wh_top_menu_and_indexterms_link',
} as const;

/** Validation limits */
export const LIMITS = {
  MAX_QUESTIONS_PER_PAGE: 100,
  MAX_CELL_CONTENT_LENGTH: 500,
  MAX_NAME_LENGTH: 100,
  MAX_SERVICE_ID_LENGTH: 10,
} as const;
